import { Backdrop, Box, Button, CircularProgress, Container, makeStyles, MuiThemeProvider, Paper, Typography, useTheme } from '@material-ui/core';
import clsx from 'clsx';
import useCustomModal from 'components/CustomConfirm';
import CustomConfirmDialog from 'components/CustomConfirm/CustomConfirmDialog';
import { useConfirm } from 'material-ui-confirm';
import moment from "moment-timezone";
import { NotFound } from 'pages/NotFound';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  CircularProgressbarWithChildren
} from "react-circular-progressbar";
import "react-circular-progressbar/dist/styles.css";
import { useRecoilState, useRecoilValue, useSetRecoilState } from 'recoil';
import { endAppointment, extendAppointment, getExtensions } from 'services/booking.service';
import themes from 'src/themes';
import { BrandonG } from 'themes/theme';
import { localizeCurrency } from 'util/localizeFormat';
import { useTrackEvent, useTrackPage } from 'util/trackEvent';
import { clientPulseState, STEP_STATE_LIVE } from '../RecoilState';
import { stepState } from '../RecoilState';
import { STEP_STATE_PAYMENT } from '../RecoilState';
import { clientState } from '../RecoilState';
import './anims.scss';
import { BookingNotFound } from '../Details/BookingNotFound';


const useStyles = makeStyles((theme) => {
  return {
    backdrop: {
      zIndex: theme.zIndex.drawer + 1,
      color: '#fff',
    },
    box: {
      height: '100vh',
    },
    serviceContentImg: {
      padding: theme.spacing(5),
    },
    content: {
      width: '100%',
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
      justifyContent: "center",
      alignItems: "center",
    },
    header: {
      textTransform: 'uppercase',
      fontWeight: '300',
    },
    enlarge: {
      fontSize: '1.5rem'
    },
    circlecontent: {
      padding: theme.spacing(12),

    },
    imgFit: {
      width: '100%',
      objectFit: 'scale-down'
    },
    actions: {
      display: 'flex',
      flexDirection: 'row',
      flexWrap: 'wrap',
      justifyContent: 'center',
      width: '100%',
      "& >*": {
        margin: theme.spacing(2),
        width: "30%"
      }
    },
    bigHeader: {
      fontFamily: BrandonG,
      fontSize: "2rem",
      fontWeight: 500,
      textAlign: "center",
    },
    padded: {
      marginBottom: theme.spacing(2),
    },
    twoliner: {
      display: 'flex',
      flexDirection: 'column',
    },
    whiteoverride: {
      color: 'white',
    }
  }
})

const OngoingJob = ({ match, location }) => {
  const classes = useStyles()
  const theme = useTheme()
  const serviceId = match?.params?.serviceId
  const clientData = useRecoilValue(clientState);

  const setStepState = useSetRecoilState(stepState)
  const clientPulse = useRecoilValue(clientPulseState)

  const confirm = useCustomModal()
  const [extendError, setExtendError] = useState();
  const [timeRemaining, setTimeRemaining] = useState(0);
  const [loading, setLoading] = useState(false);

  const trackEvent = useTrackEvent()

  const trackPage = useTrackPage()
  useEffect(() => {
    trackPage('Ongoing Job')
    setStepState(STEP_STATE_LIVE)
  }, []);


  const percentTimeRemaining = useMemo(() => {
    const startDate = moment(clientData?.appointment?.current_job?.start_date)
    const durationInMinutes = clientData?.appointment?.current_job?.duration * 60
    const now = moment()
    const diffInSec = now.diff(startDate, 'seconds')
    let diffinMin = Math.floor((durationInMinutes / 60) - now.diff(startDate, 'minutes'))
    diffinMin = diffinMin < 0 ? 0 : diffinMin

    const percent = diffInSec / durationInMinutes
    setTimeRemaining(diffinMin)
    return percent * 100

  }, [clientData, clientPulse]);

  const doCancel = useCallback(
    async () => {
      await endAppointment(clientData?.appointment?.id)
      setLoading(true)
    },
    [clientData?.appointment?.id],
  );

  const dialogExtensionFailed = useCallback(
    async (error) => {
      confirm(props => (
        <CustomConfirmDialog {...props}
          description={<Typography>{error?.response?.data?.message?.en}</Typography>}
          title='Extension failed'
          confirmationText='Okay'
          onConfirm={() => {
            trackEvent('Ongoing - Extension Failed - Okay ', {dialog: 'dialogExtensionFailed' })
            props.onClose()
          }}
          noCancel
        />
      ))
    },
    [confirm],
  );


  const dialogExtensionSuccess = useCallback(
    async ({ price, duration }) => {
      confirm(props => (
        <CustomConfirmDialog {...props}
          description={<Typography>You have successfully added {duration} minutes. <br />
            You will be charged additional {localizeCurrency(price)} </Typography>}
          title='Booking extended'
          confirmationText='Okay'
          onConfirm={() => {
            trackEvent('Ongoing - Booking extended - Okay ', { dialog: 'dialogExtensionSuccess' })
            props.onClose()
          }}
          noCancel
        />
      ))
    },
    [confirm],
  );

  const doExtend = useCallback(
    async (duration, price, props) => {
      try {
        await extendAppointment(clientData?.appointment?.id, duration)
        dialogExtensionSuccess({ duration, price })
      } catch (e) {
        setExtendError(e)
        trackEvent('Ongoing - open extension failed dialog ', {
          dialog: 'dialogExtensionFailed',
          error: {
            message: e?.message,
            message_en: e?.response?.data?.message?.en,
            details: e?.response?.data?.details,
            code: e?.response?.data?.code,
          }
        })
        dialogExtensionFailed(e)
      }
    },
    [clientData?.appointment?.id, dialogExtensionFailed],
  );

  const confirmCancel = useCallback(
    async () => {
      confirm(props => (
        <CustomConfirmDialog {...props}
          description={<Typography>You are ending the service early.<br />Are you sure you want to end it now?</Typography>}
          title='Would you like to end the service?'
          onConfirm={() => {
            trackEvent('Ongoing - Extension Confirmation - OK ', {dialog: 'confirmCancel' })
            doCancel()
          }}
        />
      ))
    },
    [confirm, doCancel],
  );


  const confirmExtension = useCallback(
    async () => {
      setExtendError()
      const extensions = await getExtensions(clientData?.appointment?.id)
      confirm(props => (
        <CustomConfirmDialog {...props}
          description={<>
            {extensions?.needs_confirmation && <Typography className={classes.padded}>Your provider needs to confirm your request before the service starts</Typography>}
            {(extensions?.list ?? []).map((extension, index) => {
              const { price, duration } = extension
              return <Button className={classes.padded} fullWidth variant="contained" color="primary" key={index} onClick={async () => {
                trackEvent('Ongoing Extend dialog ', {dialog: 'confirmExtension', duration, price })
                await doExtend(duration, price, props)
              }}>
                <div className={classes.twoliner}>
                  <Typography className={classes.whiteoverride}>{duration} minutes</Typography>
                  <Typography className={classes.whiteoverride} variant="caption">Additional {localizeCurrency(price)}</Typography>
                </div>
              </Button>
            })}

          </>}
          title='Extend'
          noCancel

        />
      ))
    },
    [classes.padded, classes.twoliner, classes.whiteoverride, clientData?.appointment?.id, confirm, doExtend],
  );

  if (!serviceId) {
    return <BookingNotFound />
  }


  return (
    <>
      <Backdrop className={classes.backdrop} open={loading}>
        <CircularProgress color="inherit" />
      </Backdrop>
      <Paper square elevation={0} className={clsx(classes.box, 'ongoinggradient')}>
        <Container maxWidth="md" className={classes.content}>
          <Typography className={clsx(classes.header)} align="center" variant="h1" gutterBottom>Service On going</Typography>
          <Typography className={classes.enlarge} align="center" variant="body2" gutterBottom>{clientData?.appointment?.type?.label}</Typography>
          <Container maxWidth="sm">
            {clientData?.appointment?.type?.category === 'WELLNESS' ? (
              <CircularProgressbarWithChildren
                className={classes.serviceContentImg}
                value={percentTimeRemaining}
                strokeWidth={2}
                styles={{
                  root: {},
                  path: {
                    stroke: "#FFF",
                    strokeWidth: 1.1
                  },
                  trail: {
                    stroke: theme.palette.error.light,
                    strokeWidth: 1
                  }
                }}
              >
                <div className={clsx(classes.circlecontent, classes.content)}>
                  <img className={classes.imgFit} src={clientData?.appointment?.type?.icon_url} />
                  <Typography variant="h4" gutterBottom>{timeRemaining} minutes</Typography>
                </div>
              </CircularProgressbarWithChildren>
            ) : (
              <div className={clsx(classes.circlecontent, classes.content)}>
                <img className={classes.imgFit} src={clientData?.appointment?.type?.icon_url} />
                <Typography variant="h4" gutterBottom>Stay calm while provider does sample collection</Typography>
              </div>
            )}
            {clientData?.appointment?.type?.category === 'WELLNESS' &&
              <Box className={classes.actions}>
                <Button size="large" variant="contained" color="default" onClick={() => {
                  trackEvent('Ongoing - Stop - click ', {})
                  confirmCancel()
                }}>Stop</Button>
                <Button size="large" variant="contained" color="secondary" onClick={() => {
                  trackEvent('Ongoing - Extend - click  ', {})
                  confirmExtension()
                }}>Extend</Button>
              </Box>
            }
          </Container>

        </Container>
      </Paper>
    </>
  )
}

const WrappedCard = (props) => {
  return (
    <MuiThemeProvider theme={themes.dark}>
      <OngoingJob {...props} />
    </MuiThemeProvider>
  )
}

export { WrappedCard as OngoingJob };

